/* The letters from customers (see board.tsx), over the screen. */

.board {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(255, 246, 235, 0.55);
  backdrop-filter: blur(3px);
  z-index: 10;
}

.card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;
  width: min(520px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  padding: 18px 22px 20px;
  border-radius: 24px;
  background: var(--panel);
  border: 1px solid var(--line);
  box-shadow: 0 12px 40px rgba(120, 80, 50, 0.15);
  animation: rise-in 0.3s ease-out;
}

@keyframes rise-in {
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

.heading {
  margin: 0;
  font-size: 20px;
  text-align: center;
}

.letter {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* The letters as tiles, three to a row, each level on rows of its own. */
.list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.level {
  grid-column: 1 / -1;
  margin: 6px 0 -2px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--ink-soft);
}

/* An envelope: a picture of what it asks for, title and sender, the wish on a tag. */
.item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 0;
  min-height: 100%;
  padding: 8px 8px 10px;
  border: 1px solid var(--line);
  border-top: 4px solid var(--accent);
  border-radius: 12px;
  background: #fffaf3;
  font: inherit;
  text-align: center;
  color: var(--ink);
  cursor: pointer;
  transition: transform 0.1s, box-shadow 0.1s;
}

.thumb {
  position: relative;
  display: grid;
  place-items: center;
  width: 84px;
  height: 84px;
  margin-bottom: 2px;
  border-radius: 50%;
  background: radial-gradient(circle, #f3fbf8 55%, transparent 70%);
}

.thumb img {
  width: 100%;
  height: 100%;
}

.item:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(120, 80, 50, 0.12);
}

/* The customer's face in the corner, across from the rank. */
.itemFace {
  position: absolute;
  top: 5px;
  left: 5px;
  width: 30px;
  height: 30px;
}

/* The best delivery's rank in the corner, coloured as in the ledger. */
.itemRank {
  position: absolute;
  top: 6px;
  right: 6px;
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--accent);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.itemRank[data-rank="S"],
.itemRank[data-rank="SS"] {
  background: linear-gradient(135deg, #f7c948, #e8962e);
  box-shadow: 0 0 0 2px rgba(247, 201, 72, 0.35);
}

.itemRank[data-rank="SS"] {
  font-size: 10px;
}

.itemRank[data-rank="B"],
.itemRank[data-rank="C"],
.itemRank[data-rank="D"] {
  background: var(--mint);
}

.itemTitle {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
}

.itemClient {
  font-size: 11px;
  color: var(--ink-soft);
}

.itemWish {
  margin-top: auto;
  padding: 2px 8px;
  border-radius: 99px;
  background: var(--accent-soft);
  font-size: 11px;
  font-weight: 700;
  color: var(--accent);
  white-space: nowrap;
}

/* The wish, and the air hoped for besides (looked at by the VLM, not counted), side by side. */
.wishes {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 8px;
}

.wish {
  margin: 0;
  align-self: center;
  padding: 4px 12px;
  border-radius: 99px;
  background: var(--accent-soft);
  font-size: 13px;
  font-weight: 700;
  color: var(--accent);
}

.atmosphere {
  background: rgba(154, 130, 224, 0.16);
  color: #7a62c4;
}

.actions button {
  flex: 1;
}

@media (max-width: 480px) {
  .list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.progress,
.notice {
  grid-column: 1 / -1;
  margin: 4px 0;
  font-size: 12px;
  line-height: 1.6;
  color: var(--ink-soft);
}
.notice {
  color: #925322;
}
.challenge {
  padding: 12px;
  border-radius: 12px;
  background: var(--accent-soft);
  font-size: 13px;
  line-height: 1.6;
}
.challenge p {
  margin: 6px 0 0;
}
.kind {
  font-size: 11px;
  font-weight: 700;
  color: var(--accent);
}
.lock {
  font-size: 11px;
  line-height: 1.5;
  color: var(--ink-soft);
}
.item:disabled {
  cursor: default;
  background: #f0ebe4;
  border-top-color: #b8b0a6;
  box-shadow: none;
  transform: none;
}
/* drawThumb renders only the model onto transparency; keep the card and text clear. */
.item[data-locked="true"] .thumb img {
  filter: brightness(0);
  opacity: 0.6;
}

.item[data-locked="true"] .thumb {
  background: radial-gradient(circle, #eee3d5 55%, transparent 70%);
}

.lockBadge {
  position: absolute;
  right: 0;
  bottom: 0;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border: 1px solid var(--line);
  border-radius: 50%;
  background: #fffaf3;
  color: var(--ink);
}

.lockBadge svg {
  width: 15px;
  height: 15px;
}

.lockLabel {
  display: block;
  margin-bottom: 2px;
  font-weight: 700;
}

.item[data-locked="true"] .lock {
  color: var(--ink);
  overflow-wrap: anywhere;
}

.item:focus-visible {
  outline: 2px solid var(--ink);
  outline-offset: 2px;
}
.itemWish {
  white-space: normal;
}
